import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppResource } from '../../context/AppDataContext';
import { outletService } from '../../services/outletService';
import { organizationService } from '../../services/organizationService';
import { userService } from '../../services/userService';
import DataGrid, { actionsColumn, dash } from '../../components/DataGrid';
import './Outlets.css';
import { PageLoader } from '../../components/Loader';
import BulkImportPanel from '../../components/BulkImportPanel';
import { OrganizationPicker } from '../../components/Pickers';
import { toast } from '../../utils/toast';
import { StatCards } from '../../components/ScheduleKpis';
import { auditService } from '../../services/auditService';
import { outletKpis } from '../../utils/listKpis';

const OUTLET_COLUMNS = [
  { key: 'organization', label: 'Organization', required: true, hint: 'name or brand code', aliases: ['organization name', 'organisation', 'organisation name', 'client', 'client name', 'brand', 'company'] },
  { key: 'outlet_code', label: 'Outlet code', aliases: ['store code', 'site code', 'code', 'outlet id', 'store id', 'site id'] },
  { key: 'name', label: 'Outlet name', required: true, aliases: ['outlet', 'store', 'store name', 'site', 'site name', 'name', 'branch'] },
  { key: 'city', label: 'City', aliases: ['town', 'location'] },
  { key: 'state', label: 'State', aliases: ['region'] },
  { key: 'pincode', label: 'Pincode', aliases: ['pin', 'pin code', 'zip', 'zip code', 'postal code'] },
  { key: 'address', label: 'Address', aliases: ['outlet address', 'store address'], showInReport: false },
  { key: 'email', label: 'Email', aliases: ['email id', 'store email'], showInReport: false },
  { key: 'phone', label: 'Phone', aliases: ['phone number', 'mobile', 'contact', 'store phone'], showInReport: false },
];
const OUTLET_SAMPLE = [
  { organization: 'Pantaloons', outlet_code: 'PT-001', name: 'Pantaloons Andheri', city: 'Mumbai', state: 'Maharashtra', pincode: '400053', address: 'Infiniti Mall, Andheri West', email: '', phone: '' },
  { organization: 'PT', outlet_code: 'PT-002', name: 'Pantaloons Thane', city: 'Thane', state: 'Maharashtra', pincode: '400601', address: '', email: '', phone: '' },
];

function checkOutletRow(row) {
  if (!row.organization) return 'Organization is missing';
  if (!row.name) return 'Outlet name is missing';
  if (row.outlet_code.length > 50) return 'Outlet code is longer than 50 characters';
  if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) return 'Email is not valid';
  if (row.phone && row.phone.length > 20) return 'Phone is longer than 20 characters';
  if (row.pincode && row.pincode.length > 20) return 'Pincode is longer than 20 characters';
  return '';
}

const emptyOutletForm = {
  organization_id: '',
  outlet_code: '',
  name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
};

function filterAuditors(users) {
  return users.filter(
    (user) =>
      user.role_name === 'auditor' ||
      user.role_name === 'super_admin' ||
      user.role_name === 'admin',
  );
}

/** Outlets (stores). Embedded = the "Stores" tab of Clients & Stores; initialOrgId pre-filters by client. */
function Outlets({ embedded = false, initialOrgId = 'all' }) {
  const loadOutlets = useCallback(() => outletService.getAll(), []);
  const loadOrganizations = useCallback(() => organizationService.getAll(), []);
  const loadUsers = useCallback(() => userService.getAll(), []);
  const {
    data: outlets = [],
    loading: outletsLoading,
    error: outletsError,
    refresh: refreshOutlets,
  } = useAppResource('outlets', loadOutlets, []);
  const {
    data: organizations = [],
    loading: organizationsLoading,
    error: organizationsError,
  } = useAppResource('organizations', loadOrganizations, []);
  const {
    data: allUsers = [],
    loading: usersLoading,
    error: usersError,
  } = useAppResource('users', loadUsers, []);
  const [error, setError] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const loadSchedules = useCallback(() => auditService.getAll(), []);
  const { data: schedules = [] } = useAppResource('audits:kpi', loadSchedules, []);
  // Bulk upload preview: also flag organizations that don't exist yet.
  const checkBulkRow = useCallback(
    (row) => {
      const problem = checkOutletRow(row);
      if (problem) return problem;
      const name = row.organization.trim().toLowerCase();
      const code = row.organization.trim().toUpperCase();
      const found = organizations.some(
        (org) => String(org.name || '').trim().toLowerCase() === name || String(org.brand_code || '').toUpperCase() === code,
      );
      return found ? '' : `Organization "${row.organization}" not found - add it on the Organizations page first`;
    },
    [organizations],
  );
  const [selectedOrgId, setSelectedOrgId] = useState(initialOrgId || 'all');
  useEffect(() => {
    setSelectedOrgId(initialOrgId || 'all');
  }, [initialOrgId]);

  const [auditorModalOpen, setAuditorModalOpen] = useState(false);
  const [auditorModalOutlet, setAuditorModalOutlet] = useState(null);
  const [assignedAuditors, setAssignedAuditors] = useState([]);
  const [savingAuditors, setSavingAuditors] = useState(false);

  const [outletFormOpen, setOutletFormOpen] = useState(false);
  const [editingOutlet, setEditingOutlet] = useState(null);
  const [outletForm, setOutletForm] = useState(emptyOutletForm);
  const [savingOutlet, setSavingOutlet] = useState(false);

  const visibleOutlets = useMemo(() => {
    if (selectedOrgId === 'all') {
      return outlets;
    }
    return outlets.filter(
      (outlet) =>
        String(outlet.organization_id || 'unassigned') ===
        String(selectedOrgId),
    );
  }, [outlets, selectedOrgId]);

  const organizationsForFilter = useMemo(
    () =>
      organizations.map((org) => ({
        id: org.id,
        name: org.name,
      })),
    [organizations],
  );

  const availableAuditors = useMemo(() => {
    const allAuditors = filterAuditors(allUsers);
    const assignedIds = new Set(assignedAuditors.map((user) => user.id));
    return allAuditors.filter((user) => !assignedIds.has(user.id));
  }, [allUsers, assignedAuditors]);

  const loading = outletsLoading || organizationsLoading || usersLoading;
  const visibleError = error || outletsError || organizationsError || usersError;

  const openCreateOutletForm = () => {
    setEditingOutlet(null);
    setOutletForm({
      ...emptyOutletForm,
      organization_id:
        selectedOrgId !== 'all'
          ? String(selectedOrgId)
          : organizations[0]
            ? String(organizations[0].id)
            : '',
    });
    setOutletFormOpen(true);
  };

  const openEditOutletForm = (outlet) => {
    setEditingOutlet(outlet);
    setOutletForm({
      organization_id: outlet.organization_id ? String(outlet.organization_id) : '',
      outlet_code: outlet.outlet_code || '',
      name: outlet.name || '',
      email: outlet.email || '',
      phone: outlet.phone || '',
      address: outlet.address || '',
      city: outlet.city || '',
      state: outlet.state || '',
      pincode: outlet.pincode || '',
    });
    setOutletFormOpen(true);
  };

  const closeOutletForm = () => {
    setOutletFormOpen(false);
    setEditingOutlet(null);
    setOutletForm(emptyOutletForm);
  };

  const handleOutletFormChange = (event) => {
    const { name, value } = event.target;
    setOutletForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOutletSubmit = async (event) => {
    event.preventDefault();
    setSavingOutlet(true);
    setError('');

    const payload = {
      organization_id: Number(outletForm.organization_id),
      outlet_code: outletForm.outlet_code.trim() || null,
      name: outletForm.name.trim(),
      email: outletForm.email.trim() || null,
      phone: outletForm.phone.trim() || null,
      address: outletForm.address.trim() || null,
      city: outletForm.city.trim() || null,
      state: outletForm.state.trim() || null,
      pincode: outletForm.pincode.trim() || null,
    };

    try {
      if (editingOutlet) {
        await outletService.update(editingOutlet.id, payload);
        toast.success('Store updated', payload.name);
      } else {
        await outletService.create(payload);
        toast.success('Store added', payload.name);
      }
      closeOutletForm();
      await refreshOutlets();
    } catch (err) {
      toast.error(editingOutlet ? 'Store not updated' : 'Store not added', err.message);
    } finally {
      setSavingOutlet(false);
    }
  };

  const handleDeleteOutlet = async (outlet) => {
    const confirmed = window.confirm(`Delete outlet "${outlet.name}"?`);
    if (!confirmed) {
      return;
    }

    setError('');

    try {
      await outletService.delete(outlet.id);
      toast.success('Store deleted', outlet.name);
      await refreshOutlets();
    } catch (err) {
      toast.error('Store not deleted', err.message);
    }
  };

  const openAssignAuditors = async (outlet) => {
    setAuditorModalOutlet(outlet);
    setSavingAuditors(false);
    setError('');

    try {
      const auditorsForOutlet = await outletService.getAuditors(outlet.id);
      setAssignedAuditors(auditorsForOutlet);
    } catch (err) {
      toast.error('Could not load the store’s auditors', err.message);
      setAssignedAuditors([]);
    }

    setAuditorModalOpen(true);
  };

  const closeAuditorModal = () => {
    setAuditorModalOpen(false);
    setAuditorModalOutlet(null);
    setAssignedAuditors([]);
  };

  const moveToAssigned = (user) => {
    setAssignedAuditors((prev) => {
      if (prev.some((item) => item.id === user.id)) {
        return prev;
      }
      return [...prev, user];
    });
  };

  const moveToAvailable = (user) => {
    setAssignedAuditors((prev) => prev.filter((item) => item.id !== user.id));
  };

  const handleSaveAuditors = async () => {
    if (!auditorModalOutlet) {
      return;
    }

    setSavingAuditors(true);
    setError('');

    try {
      await outletService.updateAuditors(auditorModalOutlet.id, {
        auditor_ids: assignedAuditors.map((user) => user.id),
      });
      toast.success('Auditors saved', `${assignedAuditors.length} assigned to ${auditorModalOutlet.name}`);
      closeAuditorModal();
      await refreshOutlets();
    } catch (err) {
      toast.error('Auditors not saved', err.message);
    } finally {
      setSavingAuditors(false);
    }
  };

  const auditorNames = (outlet) =>
    Array.isArray(outlet.auditors)
      ? outlet.auditors.map((auditor) => auditor.name || auditor.email).join(', ')
      : '';

  const columns = [
    { headerName: 'Outlet', field: 'name', minWidth: 180 },
    { headerName: 'Code', field: 'outlet_code', valueFormatter: (p) => dash(p.value), maxWidth: 160 },
    { headerName: 'Organization', field: 'organization_name', valueFormatter: (p) => dash(p.value) },
    { headerName: 'City', field: 'city', valueFormatter: (p) => dash(p.value), maxWidth: 180 },
    {
      headerName: 'Auditors',
      colId: 'auditors',
      valueGetter: (p) => auditorNames(p.data),
      minWidth: 220,
      wrapText: true,
      autoHeight: true,
      cellRenderer: (p) =>
        p.value ? (
          <span>{p.value}</span>
        ) : (
          <span className="outlets-page__empty-cell">No auditors assigned</span>
        ),
    },
    actionsColumn(
      (outlet) => (
        <>
          <button
            type="button"
            className="outlets-table__btn outlets-table__btn--edit"
            onClick={() => openEditOutletForm(outlet)}
          >
            Edit
          </button>
          <button
            type="button"
            className="outlets-table__btn"
            onClick={() => openAssignAuditors(outlet)}
          >
            Auditors
          </button>
          <button
            type="button"
            className="outlets-table__btn outlets-table__btn--delete"
            onClick={() => handleDeleteOutlet(outlet)}
          >
            Delete
          </button>
        </>
      ),
      { width: 240, className: 'outlets-table__actions' },
    ),
  ];

  return (
    <div className={embedded ? 'outlets-page outlets-page--embedded' : 'page outlets-page'}>
      <header className={embedded ? 'hub-section__toolbar outlets-page__header' : 'page__header outlets-page__header'}>
        <div>
          {embedded ? (
            <h2>Stores</h2>
          ) : (
            <>
              <p className="page__eyebrow">Operations</p>
              <h1>Outlets</h1>
            </>
          )}
          <p className="outlets-page__subtitle">
            Stores (outlets) of every client: filter by client and assign auditors.{' '}
            <Link to="/clients?tab=clients">Manage clients</Link>
          </p>
        </div>

        <div className="outlets-page__header-actions">
          <div className="outlets-page__filters">
            <label>
              Client
              <OrganizationPicker
                organizations={organizationsForFilter}
                value={selectedOrgId === 'all' ? '' : String(selectedOrgId)}
                onChange={(value) => setSelectedOrgId(value || 'all')}
                emptyLabel="All clients"
                allowCreate={false}
                ariaLabel="Show stores of"
              />
            </label>
          </div>

          <button type="button" className="bulk-open-btn" onClick={() => setBulkOpen(true)} disabled={organizations.length === 0}>
            ⇪ Bulk upload
          </button>
          <button
            type="button"
            className="outlets-page__add-btn"
            onClick={openCreateOutletForm}
            disabled={organizations.length === 0}
            title={
              organizations.length === 0
                ? 'Create an organization first'
                : 'Add outlet'
            }
          >
            + New Store
          </button>
        </div>
      </header>

      {!loading ? (
        <StatCards
          items={outletKpis(
            visibleOutlets,
            schedules || [],
            selectedOrgId === 'all' ? '' : organizations.find((org) => String(org.id) === String(selectedOrgId))?.name,
          )}
        />
      ) : null}

      {visibleError && (
        <div className="outlets-page__alert" role="alert">
          {visibleError}
        </div>
      )}

      {bulkOpen ? (
        <BulkImportPanel
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          title="Bulk upload stores"
          noun="store"
          nounPlural="stores"
          templateName="outlets-template.csv"
          intro="Add many outlets at once, for one or more organizations. The organization column takes the organization name exactly as on the Organizations page (or its two-letter brand code). An outlet is skipped when its organization already has the same outlet code (or, without a code, the same name)."
          columns={OUTLET_COLUMNS}
          sampleRows={OUTLET_SAMPLE}
          check={checkBulkRow}
          upload={outletService.bulkImport}
          onDone={refreshOutlets}
        />
      ) : null}

      <section className="outlets-page__card">
        {loading ? (
          <PageLoader label="Loading outlets…" />
        ) : organizations.length === 0 ? (
          <div className="outlets-page__empty-state">
            <p>Create an organization before adding outlets.</p>
            <Link to="/clients?tab=clients">Go to Clients</Link>
          </div>
        ) : visibleOutlets.length === 0 ? (
          <div className="outlets-page__empty-state">
            <p>No outlets found.</p>
            <button type="button" onClick={openCreateOutletForm}>
              Create your first outlet
            </button>
          </div>
        ) : (
          <DataGrid
            rowData={visibleOutlets}
            columnDefs={columns}
            searchPlaceholder="Search outlet, code, city or auditor…"
          />
        )}
      </section>

      {outletFormOpen && (
        <div className="outlets-modal">
          <div className="outlets-modal__backdrop" onClick={closeOutletForm} />
          <div
            className="outlets-modal__panel outlets-modal__panel--form"
            role="dialog"
            aria-modal="true"
          >
            <div className="outlets-modal__header">
              <h2>{editingOutlet ? 'Edit Outlet' : 'New Outlet'}</h2>
              <button type="button" onClick={closeOutletForm}>
                ×
              </button>
            </div>

            <form className="outlets-form" onSubmit={handleOutletSubmit}>
              <label>
                Organization
                <OrganizationPicker
                  organizations={organizations}
                  value={outletForm.organization_id ? String(outletForm.organization_id) : ''}
                  onChange={(value) => setOutletForm((prev) => ({ ...prev, organization_id: value }))}
                  required
                />
              </label>

              <div className="outlets-form__grid">
                <label>
                  Outlet name
                  <input
                    type="text"
                    name="name"
                    value={outletForm.name}
                    onChange={handleOutletFormChange}
                    required
                    placeholder="Downtown Store"
                  />
                </label>

                <label>
                  Outlet code
                  <input
                    type="text"
                    name="outlet_code"
                    value={outletForm.outlet_code}
                    onChange={handleOutletFormChange}
                    placeholder="OUT-001"
                  />
                </label>
              </div>

              <div className="outlets-form__grid">
                <label>
                  Email
                  <input
                    type="email"
                    name="email"
                    value={outletForm.email}
                    onChange={handleOutletFormChange}
                  />
                </label>

                <label>
                  Phone
                  <input
                    type="text"
                    name="phone"
                    value={outletForm.phone}
                    onChange={handleOutletFormChange}
                  />
                </label>
              </div>

              <label>
                Address
                <input
                  type="text"
                  name="address"
                  value={outletForm.address}
                  onChange={handleOutletFormChange}
                />
              </label>

              <div className="outlets-form__grid outlets-form__grid--three">
                <label>
                  City
                  <input
                    type="text"
                    name="city"
                    value={outletForm.city}
                    onChange={handleOutletFormChange}
                  />
                </label>

                <label>
                  State
                  <input
                    type="text"
                    name="state"
                    value={outletForm.state}
                    onChange={handleOutletFormChange}
                  />
                </label>

                <label>
                  Pincode
                  <input
                    type="text"
                    name="pincode"
                    value={outletForm.pincode}
                    onChange={handleOutletFormChange}
                  />
                </label>
              </div>

              <div className="outlets-modal__footer">
                <button
                  type="button"
                  className="outlets-modal__cancel"
                  onClick={closeOutletForm}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="outlets-modal__submit"
                  disabled={savingOutlet}
                >
                  {savingOutlet
                    ? 'Saving...'
                    : editingOutlet
                      ? 'Update Outlet'
                      : 'Create Outlet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {auditorModalOpen && auditorModalOutlet && (
        <div className="outlets-modal">
          <div className="outlets-modal__backdrop" onClick={closeAuditorModal} />
          <div
            className="outlets-modal__panel"
            role="dialog"
            aria-modal="true"
          >
            <div className="outlets-modal__header">
              <div>
                <h2>Assign auditors</h2>
                <p>
                  {auditorModalOutlet.name}{' '}
                  {auditorModalOutlet.organization_name
                    ? `· ${auditorModalOutlet.organization_name}`
                    : ''}
                </p>
              </div>
              <button type="button" onClick={closeAuditorModal}>
                ×
              </button>
            </div>

            <div className="outlets-modal__content">
              <div className="outlets-dual-list">
                <section className="outlets-dual-list__column">
                  <header>
                    <h3>Available auditors</h3>
                    <p>
                      {availableAuditors.length} user
                      {availableAuditors.length === 1 ? '' : 's'}
                    </p>
                  </header>
                  <div className="outlets-dual-list__list">
                    {availableAuditors.length === 0 ? (
                      <p className="outlets-dual-list__empty">
                        No more available auditors.
                      </p>
                    ) : (
                      <ul>
                        {availableAuditors.map((user) => (
                          <li key={user.id}>
                            <button
                              type="button"
                              onClick={() => moveToAssigned(user)}
                            >
                              <span className="outlets-dual-list__name">
                                {user.name || user.email}
                              </span>
                              <span className="outlets-dual-list__meta">
                                {user.email}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>

                <section className="outlets-dual-list__column">
                  <header>
                    <h3>Assigned auditors</h3>
                    <p>
                      {assignedAuditors.length} auditor
                      {assignedAuditors.length === 1 ? '' : 's'}
                    </p>
                  </header>
                  <div className="outlets-dual-list__list">
                    {assignedAuditors.length === 0 ? (
                      <p className="outlets-dual-list__empty">
                        No auditors assigned yet.
                      </p>
                    ) : (
                      <ul>
                        {assignedAuditors.map((user) => (
                          <li key={user.id}>
                            <button
                              type="button"
                              onClick={() => moveToAvailable(user)}
                            >
                              <span className="outlets-dual-list__name">
                                {user.name || user.email}
                              </span>
                              <span className="outlets-dual-list__meta">
                                {user.email}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>
              </div>
            </div>

            <footer className="outlets-modal__footer">
              <button
                type="button"
                className="outlets-modal__cancel"
                onClick={closeAuditorModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="outlets-modal__submit"
                onClick={handleSaveAuditors}
                disabled={savingAuditors}
              >
                {savingAuditors ? 'Saving...' : 'Save changes'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

export default Outlets;
